// Carry Roll Header — app identity, units indicator, basic/adv badge, app lock, settings gear.

import { Database, Settings, ShieldOff, SlidersHorizontal } from 'lucide-react';
import { useStore } from '../../store';
import { InputMode } from '@carry-roll/shared-types';
import { useAppLock, LockMenu } from '../AppLock';
import { useUI } from '../../store/uiStore';
import { activeNavId, goNav } from '../../lib/nav';
import { ThemeToggle } from './ThemeToggle';
import { useSyncNavActive } from '../../lib/useSyncNavActive';

export function Header() {
  useSyncNavActive();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const speedUnit = useStore((s) => s.speedUnit);
  const inputMode = useStore((s) => s.inputMode);
  const showLoadDemo = useStore((s) => s.showLoadDemo);
  const atSettings = activeNavId() === 'settings';
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const openSettingsDrawer = useUI((s) => s.openSettingsDrawer);
  const openDemoPicker = useUI((s) => s.openDemoPicker);

  return (
    <header
      className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 py-2 sm:py-3 border-b"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}
      role="banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={() => goNav('calc')}
          aria-label="Carry Roll home"
          className="brand-link text-xl font-bold shrink-0"
        >
          Carry Roll
        </button>
        <span className="badge shrink-0" data-testid="units-indicator">
          {distanceUnit} / {speedUnit}
        </span>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
        <span
          className="badge"
          data-testid="input-mode-badge"
          style={{
            borderColor:
              inputMode === InputMode.Advanced ? 'var(--color-accent)' : 'var(--color-border)',
          }}
        >
          {inputMode}
        </span>
        {showLoadDemo && (
          <button
            type="button"
            className="btn icon-btn"
            onClick={openDemoPicker}
            title="Load demo dataset"
            aria-label="Load demo dataset"
            data-testid="load-demo-button"
          >
            <Database size={18} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="btn icon-btn"
          onClick={openSettingsDrawer}
          title="Quick settings"
          aria-label="Open quick settings drawer"
          data-testid="quick-settings-button"
        >
          <SlidersHorizontal size={18} aria-hidden="true" />
        </button>
        <ThemeToggle />
        {isEnabled && lock ? (
          <LockMenu lock={lock} />
        ) : !isEnabled ? (
          <button
            type="button"
            className="btn icon-btn"
            onClick={enableEncryption}
            title="Enable password protection"
            aria-label="Enable password protection"
          >
            <ShieldOff size={18} aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => goNav('settings')}
          aria-label="Open settings"
          aria-current={atSettings ? 'page' : undefined}
          className="btn icon-btn"
        >
          <Settings size={18} aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}
